/**
 * CTEAIPanel — AI sidebar for the CTE Steps tab in JobEditor.
 *
 * Two capabilities:
 * 1. "Suggest CTE Steps" button + optional hints → calls generate-cte endpoint;
 *    displays suggestions as CTEStepCards; each has "Apply" button to append
 *    the step to the job's CTE list via onApplyStep() callback.
 *
 * 2. Per-step "Refine with AI" wand icon → opens inline instruction input;
 *    submitting calls refine-cte; result is passed to onStepRefined() callback
 *    to update that step's SQL in the editor in-place (unsaved until user saves).
 *
 * Safety: LLM is called with schema metadata only — never sample values.
 * This component renders only SQL expressions and step names — no data values.
 */

import { useCallback, useEffect, useState } from "react";
import {
  Loader2,
  Sparkles,
  Wand2,
  Check,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import type {
  CTESuggestionStep,
  CTEStep,
} from "../../../features/pipeline-engineer/types";
import {
  useGenerateCTE,
  useRefineCTEStep,
} from "../../../features/pipeline-engineer/useAgenticApi";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const INPUT_CLASS =
  "w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/30";

const LABEL_CLASS = "block text-xs font-medium text-white/60 mb-1";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CTEAIPanelProps {
  jobId: string;
  projectId: string;
  /** Current CTE steps in the editor — needed for refine-cte context. */
  currentCteSteps: CTEStep[];
  /** Called when user clicks "Apply" on a suggestion — appends the step. */
  onApplyStep: (step: CTESuggestionStep) => void;
  /** Called when a step's SQL is refined — caller updates that step in-place. */
  onStepRefined: (stepName: string, newSql: string) => void;
}

// ---------------------------------------------------------------------------
// CTEStepCard — suggested step from the AI
// ---------------------------------------------------------------------------

interface CTEStepCardProps {
  step: CTESuggestionStep;
  onApply: (step: CTESuggestionStep) => void;
}

function CTEStepCard({ step, onApply }: CTEStepCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [applied, setApplied] = useState(false);

  const handleApply = useCallback(() => {
    onApply(step);
    setApplied(true);
  }, [step, onApply]);

  return (
    <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-1.5 flex-1 text-left"
          aria-expanded={expanded}
        >
          {expanded ? (
            <ChevronDown
              size={12}
              className="text-white/40 flex-shrink-0"
              aria-hidden="true"
            />
          ) : (
            <ChevronRight
              size={12}
              className="text-white/40 flex-shrink-0"
              aria-hidden="true"
            />
          )}
          <span className="text-xs font-mono font-semibold text-white/80">
            {step.stepName}
          </span>
          {step.description !== null && (
            <span className="text-xs text-white/30 truncate">
              {step.description}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={handleApply}
          disabled={applied}
          aria-label={`Apply CTE step ${step.stepName}`}
          className="flex items-center gap-1 px-2 py-1 bg-brand-500/20 hover:bg-brand-500/30 text-brand-300 rounded text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
        >
          {applied ? (
            <>
              <Check size={11} aria-hidden="true" />
              Applied
            </>
          ) : (
            "Apply"
          )}
        </button>
      </div>
      {expanded && (
        <div className="border-t border-white/10 px-3 py-2">
          <pre className="text-xs font-mono text-white/60 whitespace-pre-wrap break-all bg-black/20 rounded p-2 max-h-32 overflow-y-auto">
            {step.sqlBody}
          </pre>
          {step.safetyError !== null && (
            <p className="text-amber-400 text-xs mt-1" role="alert">
              Safety note: {step.safetyError}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RefineStepInline — per-existing-step refine UI
// ---------------------------------------------------------------------------

interface RefineStepInlineProps {
  step: CTEStep;
  jobId: string;
  projectId: string;
  currentCteSteps: CTEStep[];
  onStepRefined: (stepName: string, newSql: string) => void;
}

function RefineStepInline({
  step,
  jobId,
  projectId,
  currentCteSteps,
  onStepRefined,
}: RefineStepInlineProps) {
  const [open, setOpen] = useState(false);
  const [instruction, setInstruction] = useState("");
  const refine = useRefineCTEStep(jobId, projectId);

  const handleRefine = useCallback(async () => {
    if (!instruction.trim()) return;
    await refine.refine(
      step.stepName,
      step.sqlBody,
      instruction.trim(),
      currentCteSteps,
    );
  }, [instruction, step, currentCteSteps, refine]);

  // Apply refinement result as soon as it arrives — in an effect to avoid side effects during render
  useEffect(() => {
    if (refine.result === null) return;
    const refined = refine.result.updatedSteps.find(
      (s) => s.stepName === step.stepName,
    );
    if (refined !== undefined && refined.sqlBody !== step.sqlBody) {
      onStepRefined(step.stepName, refined.sqlBody);
      refine.reset();
      setOpen(false);
      setInstruction("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refine.result]);

  return (
    <div className="inline-flex flex-col items-start">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Refine CTE step ${step.stepName} with AI`}
        title="Refine with AI"
        className="p-1.5 text-white/30 hover:text-brand-400 hover:bg-brand-500/10 rounded transition-colors"
      >
        <Wand2 size={12} aria-hidden="true" />
      </button>
      {open && (
        <div className="mt-1 w-64 bg-black/60 border border-white/10 rounded-lg p-3 shadow-lg space-y-2 z-10">
          <label htmlFor={`refine-${step.stepName}`} className={LABEL_CLASS}>
            What should change?
          </label>
          <textarea
            id={`refine-${step.stepName}`}
            rows={2}
            className={`${INPUT_CLASS} text-xs`}
            placeholder="e.g. Add a column that calculates days since order"
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
          />
          {refine.error !== null && (
            <p className="text-red-400 text-xs" role="alert">
              {refine.error}
            </p>
          )}
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setInstruction("");
                refine.reset();
              }}
              className="text-xs text-white/40 hover:text-white/70 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleRefine()}
              disabled={refine.loading || !instruction.trim()}
              className="flex items-center gap-1 px-2 py-1 bg-brand-500 hover:bg-brand-600 text-white rounded text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {refine.loading ? (
                <Loader2
                  size={11}
                  className="animate-spin"
                  aria-hidden="true"
                />
              ) : (
                <Sparkles size={11} aria-hidden="true" />
              )}
              Refine
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function CTEAIPanel({
  jobId,
  projectId,
  currentCteSteps,
  onApplyStep,
  onStepRefined,
}: CTEAIPanelProps) {
  const [hintsOpen, setHintsOpen] = useState(false);
  const [hints, setHints] = useState("");
  const generate = useGenerateCTE(jobId, projectId);

  const handleGenerate = useCallback(async () => {
    await generate.generate(hints.trim() || null);
  }, [hints, generate]);

  return (
    <div className="bg-white/3 border border-white/10 rounded-xl p-4 space-y-3">
      {/* Panel header */}
      <div className="flex items-center gap-2">
        <Sparkles size={14} className="text-brand-400" aria-hidden="true" />
        <span className="text-xs font-semibold text-white/70">
          AI — CTE Assistant
        </span>
      </div>

      {/* Current steps: per-step refine buttons */}
      {currentCteSteps.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs text-white/40">Refine existing steps:</p>
          {currentCteSteps.map((step) => (
            <div key={step.stepName} className="flex items-center gap-2">
              <span className="text-xs font-mono text-white/50 truncate flex-1">
                {step.stepName}
              </span>
              <RefineStepInline
                step={step}
                jobId={jobId}
                projectId={projectId}
                currentCteSteps={currentCteSteps}
                onStepRefined={onStepRefined}
              />
            </div>
          ))}
        </div>
      )}

      {/* Divider */}
      {currentCteSteps.length > 0 && (
        <div className="border-t border-white/10" />
      )}

      {/* Generate CTE steps */}
      <div className="space-y-2">
        <button
          type="button"
          onClick={() => setHintsOpen((v) => !v)}
          className="text-xs text-white/40 hover:text-white/60 transition-colors"
        >
          {hintsOpen ? "Hide hints" : "Add hints (optional)"}
        </button>

        {hintsOpen && (
          <div>
            <label htmlFor="cte-ai-hints" className={LABEL_CLASS}>
              Hints for the AI
            </label>
            <textarea
              id="cte-ai-hints"
              rows={2}
              className={`${INPUT_CLASS} text-xs`}
              placeholder="e.g. Focus on order aggregation and date ranges"
              value={hints}
              onChange={(e) => setHints(e.target.value)}
            />
          </div>
        )}

        {generate.error !== null && (
          <p className="text-red-400 text-xs" role="alert">
            {generate.error}
          </p>
        )}

        <button
          type="button"
          onClick={() => void handleGenerate()}
          disabled={generate.loading}
          className="flex items-center gap-1.5 w-full px-3 py-2 bg-brand-500/20 hover:bg-brand-500/30 text-brand-300 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed justify-center"
        >
          {generate.loading ? (
            <Loader2 size={12} className="animate-spin" aria-hidden="true" />
          ) : (
            <Sparkles size={12} aria-hidden="true" />
          )}
          {generate.loading ? "Generating…" : "Suggest CTE Steps"}
        </button>
      </div>

      {/* Suggestions list */}
      {generate.result !== null && generate.result.steps.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs text-white/40">
              {generate.result.steps.length} step
              {generate.result.steps.length !== 1 ? "s" : ""} suggested
            </p>
            <button
              type="button"
              onClick={() => generate.reset()}
              className="text-xs text-white/30 hover:text-white/50 transition-colors"
            >
              Clear
            </button>
          </div>
          {generate.result.steps.map((step, idx) => (
            <CTEStepCard
              key={`${step.stepName}-${idx}`}
              step={step}
              onApply={onApplyStep}
            />
          ))}
        </div>
      )}

      {generate.result !== null && generate.result.steps.length === 0 && (
        <p className="text-xs text-white/30 text-center py-2">
          No CTE steps suggested. Try adding more column mappings first.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Export RefineStepInline for use in CTEStepPanel per-step rows
// ---------------------------------------------------------------------------

export { RefineStepInline };
